/**
 * Session and checkout draft (04-architecture § 12.2).
 *
 * The token lives in the OtterAuth SDK's storage (`localStorage` on web, an
 * accepted risk mitigated by the strict CSP); this store mirrors it in memory so
 * `getToken()` stays synchronous for the API client.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { OrderItemDraft } from '@cowoo/shared/checkout';

interface SessionState {
  token: string | null;
  userId: string | null;
  setSession(token: string, userId: string): void;
  clear(): void;
}

export const useSessionStore = create<SessionState>((set) => ({
  token: null,
  userId: null,
  setSession: (token, userId) => set({ token, userId }),
  clear: () => set({ token: null, userId: null }),
}));

interface CheckoutState {
  items: OrderItemDraft[];
  /** Created with the intent and reused for every retry (§ 8.5). */
  idempotencyKey: string | null;
  orderId: string | null;
  setItems(items: OrderItemDraft[]): void;
  setKey(key: string): void;
  setOrder(orderId: string | null): void;
  reset(): void;
}

export const useCheckoutStore = create<CheckoutState>()(
  persist(
    (set) => ({
      items: [],
      idempotencyKey: null,
      orderId: null,
      setItems: (items) => set({ items }),
      setKey: (idempotencyKey) => set({ idempotencyKey }),
      setOrder: (orderId) => set({ orderId }),
      reset: () => set({ items: [], idempotencyKey: null, orderId: null }),
    }),
    // Persisted so a reload mid-checkout resumes the same order rather than
    // creating a second one.
    { name: 'cowoo.checkout.v1' },
  ),
);
